import { VerdictConfig, VerdictOptions, VerdictOverrideConfigFiltersToFind } from '@yandex-int/k-common';
import cx from 'classnames';
import { Pause, Play, RobotRefresh } from 'education-icons';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { AnyAction } from 'redux';

import { getFirstVerdictConfigOverrideByFilters } from '../../../utils/verdict-config/verdict-config';
import { getVerdictConfig, getVerdictThemes } from '../../app-factory/selectors';
import { ProblemBarMessage } from '../../problem-bar-message/problem-bar-message';
import { RobotContext, useRobotSelector } from '../context';
import { ROBOT_FINISH_STATUS, ROBOT_RUN_STATUS } from '../robot.constants';
import { ROBOT_TABS } from '../robot.types';
import { actions } from '../useRobotRun';

// @ts-ignore
import styles from './robot__code-light-v2-controls.module.scss';

interface Props {
    dispatch: React.Dispatch<AnyAction>;
    onControlsClick?: RobotContext['onControlsClick'];
    onboardingSrc?: string;
    status: ROBOT_RUN_STATUS;
    setCurrentTab?: RobotContext['setCurrentTab'];
}

const ROBOT_MESSAGE_PREFIX = 'robot.sampleSwitch.verdict.';
const ROBOT_VERDICT_OVERRIDE_ROVER_KEY = 'rover_light';

export const Controls = ({ dispatch, onControlsClick, onboardingSrc, status, setCurrentTab }: Props) => (
    <div
        className={cx(styles.controls, { [styles.loading]: status === ROBOT_RUN_STATUS.LOADING }, 'qa_robot-controls')}
    >
        {(() => {
            switch (status) {
                case ROBOT_RUN_STATUS.FINISH:
                    return (
                        <button
                            className={cx(styles.button, styles.button_play, 'qa_robot-refresh-button')}
                            onClick={() => {
                                dispatch(actions.stop());
                                onControlsClick?.('reload');
                            }}
                            type="button"
                        >
                            <RobotRefresh />
                        </button>
                    );
                case ROBOT_RUN_STATUS.PLAY:
                    return (
                        <button
                            className={cx(styles.button, styles.button_play, 'qa_robot-pause-button')}
                            onClick={() => {
                                dispatch(actions.pause());
                                onControlsClick?.('pause');
                            }}
                            type="button"
                        >
                            <Pause />
                        </button>
                    );
                default:
                    return (
                        <button
                            className={cx(styles.button, styles.button_play, 'qa_robot-play-button')}
                            onClick={() => {
                                dispatch(actions.play());
                                setCurrentTab?.(ROBOT_TABS.CODE);
                                onControlsClick?.('play');
                            }}
                            type="button"
                        >
                            <Play style={{ marginLeft: '0.2em' }} />
                        </button>
                    );
            }
        })()}

        {onboardingSrc && (
            <button
                className={cx(styles.button, styles.button_speed, 'qa_robot-shift-button')}
                onClick={() => {
                    dispatch(actions.showOnboarding({ showOnboarding: true }));
                }}
                type="button"
            >
                ?
            </button>
        )}
    </div>
);

export const ControlsMemo = React.memo(Controls);
export const getVerdictCustomConfigByAnswerCode = (
    verdictConfig: VerdictConfig | null,
    attemptMessage: string,
    verdictConfigFilters?: VerdictOverrideConfigFiltersToFind
): VerdictOptions | undefined => {
    const getOverrideConfigByKey = (codeKey: string) => {
        const customVerdictConfig = getFirstVerdictConfigOverrideByFilters(verdictConfig, verdictConfigFilters)
            ?.config?.[codeKey];

        if (customVerdictConfig) {
            return customVerdictConfig;
        }

        return verdictConfig?.[ROBOT_VERDICT_OVERRIDE_ROVER_KEY]?.[codeKey];
    };

    const verdictCodeMessage = attemptMessage.match(/{{([a-zA-Z]+\..*)}}/)?.[1];

    if (verdictCodeMessage) {
        if (verdictCodeMessage.includes(ROBOT_MESSAGE_PREFIX)) {
            const verdictCodeKey = verdictCodeMessage.slice(ROBOT_MESSAGE_PREFIX.length);

            return getOverrideConfigByKey(verdictCodeKey);
        }
    }

    return undefined;
};

const useVerdictCustomConfig = (
    isSuccess: boolean,
    attemptMessage: string,
    verdictConfigFilters?: VerdictOverrideConfigFiltersToFind
): VerdictOptions | undefined => {
    const verdictConfig = useSelector(getVerdictConfig);

    if (isSuccess) {
        return verdictConfig?.default.rover_correct;
    }

    const verdictConfigCustom = getVerdictCustomConfigByAnswerCode(verdictConfig, attemptMessage, verdictConfigFilters);

    if (verdictConfigCustom) {
        return verdictConfigCustom;
    }

    return verdictConfig?.default.rover_incorrect;
};

const ControlsProblemBarMessage = () => {
    const intl = useIntl();
    const [isVerdictClosed, setIsVerdictClosed] = useState(false);

    const sampleKey = 0; // в заданиях ровера 2.0 будет только 1 сэмпл

    const results = useRobotSelector((s) => s.state?.results);
    const skin = useRobotSelector((s) => s.problem.markup.options.skin);
    const attempt = useRobotSelector((s) => s.attempt);
    const resultsSample = results?.result?.[sampleKey];
    const verdictThemes = useSelector(getVerdictThemes);

    const verdict = resultsSample?.check_details.verdict;

    const attemptSample = attempt?.markers?.result?.[sampleKey];
    const attemptVerdict = attemptSample?.check_details?.verdict;

    const status = verdict?.status ?? attemptVerdict?.status;
    const isSuccess = status === ROBOT_FINISH_STATUS.OK;

    const isFinished = useRobotSelector((s) => s.state?.status === ROBOT_RUN_STATUS.FINISH) || isSuccess;

    const attemptMessage = (isFinished && verdict?.msg) || attemptSample?.check_details.verdict?.msg || '';

    const showVerdict = Boolean(isFinished && status);
    const verdictConfigFilters = skin?.verdict_override_filters;

    const verdictCustomConfig = useVerdictCustomConfig(isSuccess, attemptMessage, verdictConfigFilters);

    const verdictTheme =
        verdictCustomConfig && verdictThemes?.find((theme) => theme.slug === verdictCustomConfig.theme);

    if (!showVerdict || !verdictCustomConfig || !verdictTheme) {
        return null;
    }

    let msg = verdictCustomConfig.text || '';

    if (!isSuccess && (msg.includes('Traceback') || msg.includes('SyntaxError'))) {
        msg = intl.formatMessage({ id: 'robot.syntaxError' });
    }

    const plateBorderColor: React.CSSProperties = { ['--background-plate-color' as any]: verdictTheme.borderColor };

    return (
        <div className={cx(styles.problem_bar)} style={plateBorderColor}>
            <ProblemBarMessage
                {...verdictTheme}
                animationDuration={300}
                closed={isVerdictClosed}
                onClose={() => setIsVerdictClosed(true)}
                onOpen={() => setIsVerdictClosed(false)}
                text={msg}
                theme={ProblemBarMessage.THEME.DEFAULT}
            />
            <div className={styles.plate_bg} />
        </div>
    );
};

const RobotControls = (): JSX.Element => {
    const problem = useRobotSelector((s) => s.problem);
    const onboardingSrc = problem.onboardings?.[0]?.src;

    return (
        <>
            <ControlsProblemBarMessage />
            <ControlsMemo
                dispatch={useRobotSelector((s) => s.dispatch)}
                onControlsClick={useRobotSelector((s) => s.onControlsClick)}
                onboardingSrc={onboardingSrc}
                setCurrentTab={useRobotSelector((s) => s.setCurrentTab)}
                status={useRobotSelector((s) => s.state.status)}
            />
        </>
    );
};

export default RobotControls;
